import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { Icon } from '../components/icons';
import { Empty, ErrorBox, Field, Loading, Modal, PageHead } from '../components/ui';
import { useT } from '../i18n';
import type { Client } from '../types';

export function ClientsPage() {
  const t = useT();
  const [editing, setEditing] = useState<Client | 'new' | null>(null);
  const [search, setSearch] = useState('');
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.get<Client[]>('/clients') });
  const list = (clients.data ?? []).filter((c) => !search || `${c.name} ${c.contactName ?? ''}`.toLowerCase().includes(search.toLowerCase()));
  return (
    <>
      <PageHead
        title={t('clients.title')}
        actions={
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon.plus />
            {t('clients.new')}
          </button>
        }
      />
      <div className="toolbar">
        <input className="input search" type="search" placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('common.search')} />
      </div>
      <div className="card table-wrap">
        {clients.isLoading ? (
          <Loading />
        ) : list.length === 0 ? (
          <Empty title={t('clients.emptyTitle')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('clients.name')}</th>
                <th>{t('clients.contact')}</th>
                <th>{t('clients.phone')}</th>
                <th>{t('clients.email')}</th>
                <th>{t('clients.vat')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="clickable" onClick={() => setEditing(c)}>
                  <td><strong>{c.name}</strong></td>
                  <td>{c.contactName}</td>
                  <td>{c.contactPhone}</td>
                  <td>{c.contactEmail}</td>
                  <td className="mono muted">{c.vatNumber}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <ClientModal client={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function ClientModal({ client, onClose }: { client?: Client; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: client?.name ?? '',
    contactName: client?.contactName ?? '',
    contactPhone: client?.contactPhone ?? '',
    contactEmail: client?.contactEmail ?? '',
    vatNumber: client?.vatNumber ?? '',
    currency: client?.currency ?? 'EUR',
    notes: client?.notes ?? '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name,
        contactName: f.contactName || null,
        contactPhone: f.contactPhone || null,
        contactEmail: f.contactEmail || null,
        vatNumber: f.vatNumber || null,
        currency: f.currency,
        notes: f.notes || null,
      };
      return client ? api.patch(`/clients/${client.id}`, body) : api.post('/clients', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      onClose();
    },
  });
  return (
    <Modal
      title={client ? t('clients.edit') : t('clients.new')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!f.name || save.isPending} onClick={() => save.mutate()}>{t('common.save')}</button>
        </>
      }
    >
      <ErrorBox error={save.error} />
      <div className="form-grid">
        <Field label={t('clients.name')} full htmlFor="c-name">
          <input id="c-name" className="input" value={f.name} onChange={set('name')} autoFocus />
        </Field>
        <Field label={t('clients.contact')} htmlFor="c-cn">
          <input id="c-cn" className="input" value={f.contactName} onChange={set('contactName')} />
        </Field>
        <Field label={t('clients.phone')} htmlFor="c-cp">
          <input id="c-cp" className="input" type="tel" value={f.contactPhone} onChange={set('contactPhone')} />
        </Field>
        <Field label={t('clients.email')} htmlFor="c-ce">
          <input id="c-ce" className="input" type="email" value={f.contactEmail} onChange={set('contactEmail')} />
        </Field>
        <Field label={t('clients.vat')} htmlFor="c-vat">
          <input id="c-vat" className="input" value={f.vatNumber} onChange={set('vatNumber')} />
        </Field>
        <Field label={t('clients.currency')} htmlFor="c-cur">
          <select id="c-cur" className="select" value={f.currency} onChange={set('currency')}>
            {['EUR', 'USD', 'GBP', 'CHF', 'ALL', 'RSD', 'MKD'].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label={t('common.notes')} full htmlFor="c-notes">
          <textarea id="c-notes" className="textarea" value={f.notes} onChange={set('notes')} />
        </Field>
      </div>
    </Modal>
  );
}
